import {
  Trash2, MapPin, User, Calendar, DollarSign 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import type { WasteLog } from '../../types';
import {
  WASTE_TYPE_LABELS, WASTE_REASON_LABELS, WASTE_REASON_COLORS 
} from '../../types';

interface WasteLogCardProps {
  readonly wasteLog: WasteLog;
  readonly onClick?: (wasteLog: WasteLog) => void;
}

export function WasteLogCard({
  wasteLog, onClick 
}: WasteLogCardProps) {
  const reasonColor = WASTE_REASON_COLORS[wasteLog.reason];

  const handleClick = () => {
    onClick?.(wasteLog);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.(wasteLog);
    }
  };

  return (
    <Card
      className="hover:shadow-md transition-shadow cursor-pointer hover:border-primary/50"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={onClick ? 0 : undefined}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base font-medium">{wasteLog.itemName}</CardTitle>
          </div>
          <span className={`rounded-full px-2 py-1 text-xs font-medium ${reasonColor}`}>
            {WASTE_REASON_LABELS[wasteLog.reason]}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {WASTE_TYPE_LABELS[wasteLog.type]}
          </span>
          <span className="font-semibold">
            {wasteLog.quantity} {wasteLog.unit}
          </span>
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <MapPin className="h-4 w-4" />
          <span>{wasteLog.venueName}</span>
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <User className="h-4 w-4" />
          <span>{wasteLog.loggedByName}</span>
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>{new Date(wasteLog.createdAt).toLocaleString()}</span>
        </div>

        {wasteLog.notes && (
          <p className="text-sm text-muted-foreground italic border-l-2 border-muted pl-2">
            {wasteLog.notes}
          </p>
        )}

        <div className="flex items-center justify-between pt-2 border-t">
          <div className="flex items-center gap-1 text-red-600">
            <DollarSign className="h-4 w-4" />
            <span className="font-semibold">${wasteLog.estimatedCost.toFixed(2)}</span>
          </div>
          <span className="text-xs text-muted-foreground">Estimated Cost</span>
        </div>
      </CardContent>
    </Card>
  );
}
